import { motion } from 'framer-motion'
import {
  Activity,
  AlertCircle,
  Bot,
  CheckCircle,
  Database,
  RefreshCw,
  Server,
  Users,
  XCircle,
  Zap
} from 'lucide-react'
import { useEffect, useState } from 'react'
const HealthCheck = () => {
  const [healthStatus, setHealthStatus] = useState({
    loading: false,
    lastUpdate: null,
    systemOverview: null,
    services: {
      main_app: { status: 'unknown', message: '檢查中...', response_time: null },
      ai_services: { status: 'unknown', message: '檢查中...', response_time: null },
      cases: { status: 'unknown', message: '檢查中...', response_time: null },
      notifications: { status: 'unknown', message: '檢查中...', response_time: null },
      settings: { status: 'unknown', message: '檢查中...', response_time: null },
      stats: { status: 'unknown', message: '檢查中...', response_time: null },
      taiwan_patent: { status: 'unknown', message: '檢查中...', response_time: null }
    }
  })

  // 檢查單個API端點
  const checkApiEndpoint = async (url, serviceName) => {
    const startTime = performance.now()
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        timeout: 5000
      })

      const endTime = performance.now()
      const responseTime = Math.round(endTime - startTime)

      if (response.ok) {
        const data = await response.json()
        return {
          status: 'healthy',
          message: data.status === 'healthy' ? '服務正常運行' : '服務可用',
          response_time: responseTime,
          data: data
        }
      } else {
        return {
          status: 'error',
          message: `HTTP ${response.status} - ${response.statusText}`,
          response_time: responseTime
        }
      }
    } catch (error) {
      const endTime = performance.now()
      const responseTime = Math.round(endTime - startTime)

      return {
        status: 'error',
        message: error.message.includes('fetch') ? '連接失敗' : error.message,
        response_time: responseTime
      }
    }
  }

  // 執行所有健康檢查
  const runHealthChecks = async (showLoading = true) => {
    if (showLoading) {
      setHealthStatus(prev => ({ ...prev, loading: true }))
    }

    try {
      // 使用統一健康檢查端點
      const systemResult = await checkApiEndpoint('/api/system/health')

      let services = {}
      let systemOverview = null

      if (systemResult.status === 'healthy' && systemResult.data) {
        // 從統一健康檢查響應中提取服務狀態
        const systemData = systemResult.data
        systemOverview = {
          overall_status: systemData.overall_status,
          total_services: systemData.total_services,
          healthy_services: systemData.healthy_services,
          message: systemData.message
        }

        // 轉換服務狀態格式
        Object.entries(systemData.services || {}).forEach(([serviceName, serviceData]) => {
          services[serviceName] = {
            status: serviceData.status,
            message: serviceData.status === 'healthy' ? '服務正常運行' : serviceData.response_time,
            response_time: serviceData.response_time === 'OK' ? '<50ms' : serviceData.response_time
          }
        })
      } else {
        // 如果統一端點失敗，回退到個別檢查
        const checks = [
          { key: 'ai_services', url: '/api/health', name: 'AI服務健康檢查' },
          { key: 'cases', url: '/api/cases/health', name: '案件管理服務' },
          { key: 'taiwan_patent', url: '/api/patent/health', name: '台灣專利API檢查' }
        ]

        const results = {}
        const checkPromises = checks.map(async (check) => {
          const result = await checkApiEndpoint(check.url, check.name)
          results[check.key] = result
        })

        await Promise.all(checkPromises)
        services = results
      }

      // 使用繁體中文格式化時間（24小時制）

      const timeOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false // 24小時制
      }

      setHealthStatus(prev => ({
        ...prev,
        loading: false,
        lastUpdate: new Date().toLocaleString('zh-TW', timeOptions),
        systemOverview: systemOverview,
        services: services
      }))
    } catch (error) {
      console.error('健康檢查失敗:', error)
      setHealthStatus(prev => ({
        ...prev,
        loading: false,
        lastUpdate: new Date().toLocaleString('zh-TW', { hour12: false })
      }))
    }
  }

  // 組件載入時執行健康檢查
  useEffect(() => {
    runHealthChecks(true)  // 初次載入顯示載入狀態
    // 每30秒自動更新（不顯示載入狀態避免閃爍）
    const interval = setInterval(() => runHealthChecks(false), 30000)
    return () => clearInterval(interval)
  }, [])

  // 獲取狀態圖示
  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />
      default:
        return <RefreshCw className="h-5 w-5 text-gray-400 animate-spin" />
    }
  }

  // 獲取狀態顏色 - 優化暗黑模式對比度
  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700'
      case 'error':
        return 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700'
      case 'warning':
        return 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-700'
      default:
        return 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-600'
    }
  }

  // 服務配置
  const serviceConfigs = {
    api: {
      title: 'API健康檢查',
      description: '檢查系統各項服務狀態',
      icon: Activity
    },
    user_api: {
      title: '用戶管理API',
      description: '用戶資料管理服務',
      icon: Users
    },
    ai_services: {
      title: 'AI服務狀態監控',
      description: '文件分析、智能檢索等AI服務',
      icon: Bot
    },
    patent_api: {
      title: '台灣專利API',
      description: 'TIPO專利資料庫連接服務',
      icon: Database
    }
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* 頁面標題 */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-4"
      >
        <div className="flex items-center justify-center space-x-3">
          <div className="p-3 bg-blue-100 dark:bg-blue-900/50 rounded-full">
            <Server className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{'智財權事務所AI助理平台'}</h1>
        </div>

        <div className="inline-flex items-center px-4 py-2 bg-green-100 dark:bg-green-900/40 rounded-full border border-green-200 dark:border-green-700">
          <div className="w-2 h-2 bg-green-500 dark:bg-green-400 rounded-full mr-2 animate-pulse"></div>
          <span className="text-green-800 dark:text-green-200 font-medium">{'系統運行中'}</span>
        </div>

        <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
          {'歡迎使用智財權事務所AI助理平台！這是一個基於人工智慧技術的專業智財權管理系統。'}
        </p>
      </motion.div>

      {/* 控制按鈕 */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="flex flex-col items-center space-y-4"
      >
        <div className="flex items-center space-x-4">
          <button
            onClick={() => runHealthChecks(true)}
            disabled={healthStatus.loading}
            className={`
              inline-flex items-center px-6 py-3 rounded-lg font-medium
              transition-all duration-200 shadow-sm
              ${healthStatus.loading
                ? 'bg-gray-400 dark:bg-gray-600 text-gray-200 cursor-not-allowed'
                : 'bg-blue-600 dark:bg-blue-700 text-white hover:bg-blue-700 dark:hover:bg-blue-600 hover:shadow-md transform hover:scale-105'
              }
            `}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${healthStatus.loading ? 'animate-spin' : ''}`} />
            {healthStatus.loading ? '檢查中...' : '重新檢查'}
          </button>

          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            {'重置'}
          </button>
        </div>

        {healthStatus.lastUpdate && (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {'最後更新'}: {healthStatus.lastUpdate}
          </div>
        )}
      </motion.div>

      {/* 系統概覽 */}
      {healthStatus.systemOverview && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6"
        >
          <div className="flex items-center space-x-4 mb-4">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/50 rounded-full">
              <Activity className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {'系統概覽'}
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                {healthStatus.systemOverview.message}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {healthStatus.systemOverview.total_services}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                {'總服務數'}
              </div>
            </div>

            <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {healthStatus.systemOverview.healthy_services}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                {'正常服務'}
              </div>
            </div>

            <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className={`text-2xl font-bold ${healthStatus.systemOverview.overall_status === 'healthy'
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400'
                }`}>
                {healthStatus.systemOverview.overall_status === 'healthy' ? '✓' : '✗'}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                {'系統狀態'}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* 可用服務列表 */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="space-y-4"
      >
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 flex items-center">
          <Zap className="h-5 w-5 mr-2 text-blue-600" />
          {'可用服務'}
        </h2>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4">
          {Object.entries(healthStatus.services).map(([key, status]) => {
            // 獲取服務配置
            const getServiceConfig = (serviceName) => {
              switch (serviceName) {
                case 'main_app':
                  return { title: '主應用程式', description: '核心應用服務', icon: Server }
                case 'ai_services':
                  return { title: 'AI服務', description: '人工智慧分析服務', icon: Bot }
                case 'cases':
                  return { title: '案件管理', description: '智財權案件管理服務', icon: Database }
                case 'notifications':
                  return { title: '通知服務', description: '系統通知與提醒服務', icon: Activity }
                case 'settings':
                  return { title: '系統設定', description: '平台設定管理服務', icon: Server }
                case 'stats':
                  return { title: '統計分析', description: '數據分析與報表服務', icon: Activity }
                case 'taiwan_patent':
                  return { title: '台灣專利API', description: 'TIPO專利資料庫連接服務', icon: Database }
                default:
                  return { title: serviceName, description: '系統服務', icon: Server }
              }
            }

            const config = getServiceConfig(key)
            const Icon = config.icon

            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.05 * Object.keys(healthStatus.services).indexOf(key) }}
                className={`
                  relative p-4 rounded-xl border-2 transition-all duration-300 
                  hover:shadow-lg hover:scale-105 cursor-pointer group
                  ${getStatusColor(status.status)}
                `}
              >
                {/* 狀態指示器 */}
                <div className="absolute top-2 right-2">
                  {getStatusIcon(status.status)}
                </div>

                {/* 服務圖標 */}
                <div className="flex justify-center mb-3">
                  <div className="p-3 bg-white dark:bg-gray-700 rounded-xl shadow-sm group-hover:shadow-md transition-shadow">
                    <Icon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>

                {/* 服務標題 */}
                <h3 className="text-center font-semibold text-gray-900 dark:text-gray-100 mb-2 text-sm">
                  {config.title}
                </h3>

                {/* 狀態資訊 */}
                <div className="text-center space-y-1">
                  <div className="text-xs text-gray-600 dark:text-gray-300">
                    {status.message}
                  </div>
                  {status.response_time && status.response_time !== 'OK' && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {status.response_time}
                    </div>
                  )}
                </div>

                {/* Hover 顯示詳細描述 */}
                <div className="absolute inset-0 bg-black bg-opacity-75 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center p-4">
                  <div className="text-center text-white">
                    <p className="text-sm font-medium mb-2">{config.title}</p>
                    <p className="text-xs text-gray-200">{config.description}</p>
                    <div className="mt-3 flex justify-center space-x-2">
                      <button
                        className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-xs"
                        onClick={(e) => {
                          e.stopPropagation()
                          // 跳轉到具體服務
                          const serviceUrls = {
                            'main_app': '/health',
                            'ai_services': '/api/health',
                            'cases': '/api/cases/health',
                            'notifications': '/api/notifications/health',
                            'settings': '/api/settings/health',
                            'stats': '/api/stats/health',
                            'taiwan_patent': '/api/patent/health'
                          }
                          const url = serviceUrls[key] || '/api/health'
                          window.open(url, '_blank')
                        }}
                      >
                        {'檢查'}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>
      </motion.div>

      {/* 系統資訊 */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-center text-sm text-gray-500 dark:text-gray-400 border-t dark:border-gray-700 pt-6"
      >
        <p>系統版本：2.0 | 作者：云碩科技 | 日期：2026-01-07</p>
      </motion.div>
    </div>
  )
}

export default HealthCheck
